/**
 * GetNotificationsCard Component
 *
 * The whole notification-subscription surface: one form (emails x topics x
 * organizations, every field a multi-select autocomplete) plus the list of
 * current subscriptions grouped per email and organization. Subscribing
 * creates one row per combination; removing a group deletes that email's
 * rows at that organization. Any authenticated user, limited to the org
 * scopes their claims cover.
 */

import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Box, Chip, CircularProgress, Grid, Typography } from '@mui/material';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import CloseIcon from '@mui/icons-material/Close';

import {
    InfoCard,
    Button,
    IconButton,
    MultiSelectField,
    StatusBadge,
    EmptyState,
    colors,
    typography as typographyTokens,
} from '@components/design-system';
import type { AutocompleteOption } from '@components/design-system';
import {
    useNameMapQuery,
    useNotificationTopicsQuery,
    useNotificationContactsQuery,
    useAddNotificationContactMutation,
    useRemoveNotificationContactMutation,
} from '@features/redux/apis/org';
import { Validate } from '@utils/validate';
import { SubscriptionScopeType } from '@/types/notifications';

import type { RootState } from '@features/redux/store';
import type { NotificationTopic, TopicDefinition } from '@/types/notifications';

type SubscriptionGroup = {
    email: string;
    scopeType: SubscriptionScopeType;
    scopeId: number;
    topics: NotificationTopic[];
};

const orgOptionId = (scopeType: SubscriptionScopeType, scopeId: number): string => `${scopeType}:${scopeId}`;

const parseOrgOptionId = (id: string): { ScopeType: SubscriptionScopeType; ScopeID: number } => {
    const [scopeType, scopeId] = id.split(':');
    return { ScopeType: scopeType as SubscriptionScopeType, ScopeID: Number(scopeId) };
};

/** Scope types every selected topic allows; all types when nothing is selected yet. */
const allowedScopeTypes = (selectedTopics: TopicDefinition[]): Set<SubscriptionScopeType> => {
    const allTypes = Object.values(SubscriptionScopeType);
    if (!selectedTopics.length) return new Set(allTypes);
    return new Set(
        allTypes.filter((scopeType) => selectedTopics.every((topic) => topic.scopeTypes.includes(scopeType))),
    );
};

export const GetNotificationsCard: React.FC = () => {
    const currentUserEmail = useSelector(({ auth }: RootState) => auth.TokenPayload?.email) ?? '';
    const { data: topicsData } = useNotificationTopicsQuery({});
    const { data: subscriptionRows, isLoading: subscriptionsLoading } = useNotificationContactsQuery({});
    const { data: nameMap } = useNameMapQuery({});
    const [subscribe, { isLoading: subscribing }] = useAddNotificationContactMutation();
    const [unsubscribe, { isLoading: unsubscribing }] = useRemoveNotificationContactMutation();

    const topics = topicsData?.topics ?? [];

    const [selectedEmails, setSelectedEmails] = useState<AutocompleteOption[]>([]);
    const [selectedTopicOptions, setSelectedTopicOptions] = useState<AutocompleteOption[]>([]);
    const [selectedOrgs, setSelectedOrgs] = useState<AutocompleteOption[]>([]);

    const emailSuggestions: AutocompleteOption[] = currentUserEmail
        ? [{ id: currentUserEmail.toLowerCase(), name: currentUserEmail }]
        : [];

    const topicOptions: AutocompleteOption[] = topics.map((definition) => ({
        id: definition.topic,
        name: definition.label,
    }));

    const topicLabels = useMemo(
        () =>
            topics.reduce((labels, definition) => {
                labels[definition.topic] = definition.label;
                return labels;
            }, {} as Record<NotificationTopic, string>),
        [topics],
    );

    const selectedTopicDefinitions = topics.filter((definition) =>
        selectedTopicOptions.some((option) => option.id === definition.topic),
    );
    const scopeTypesAllowed = allowedScopeTypes(selectedTopicDefinitions);

    const orgOptions = useMemo((): AutocompleteOption[] => {
        const buildOptions = (
            entries: Record<string, string> | undefined,
            scopeType: SubscriptionScopeType,
        ): AutocompleteOption[] =>
            scopeTypesAllowed.has(scopeType)
                ? Object.entries(entries ?? {}).map(([id, name]) => ({
                      id: orgOptionId(scopeType, Number(id)),
                      name: `${scopeType} · ${name}`,
                  }))
                : [];

        // Global topics (catalog events) aren't tied to an organization
        const globalOption: AutocompleteOption[] = scopeTypesAllowed.has(SubscriptionScopeType.Global)
            ? [{ id: orgOptionId(SubscriptionScopeType.Global, 0), name: 'Global · Everything' }]
            : [];

        return [
            ...globalOption,
            ...buildOptions(nameMap?.companies, SubscriptionScopeType.Company),
            ...buildOptions(nameMap?.resellers, SubscriptionScopeType.Reseller),
            ...buildOptions(nameMap?.locations, SubscriptionScopeType.Location),
        ];
    }, [nameMap, scopeTypesAllowed]);

    // Topic-scope intersection can invalidate an already-picked org
    const effectiveOrgs = selectedOrgs.filter((option) =>
        scopeTypesAllowed.has(parseOrgOptionId(option.id).ScopeType),
    );

    const scopeName = (scopeType: SubscriptionScopeType, scopeId: number): string => {
        if (scopeType === SubscriptionScopeType.Global) return 'Everything';
        const namesByScope: Partial<Record<SubscriptionScopeType, Record<string, string> | undefined>> = {
            [SubscriptionScopeType.Company]: nameMap?.companies,
            [SubscriptionScopeType.Reseller]: nameMap?.resellers,
            [SubscriptionScopeType.Location]: nameMap?.locations,
        };
        return namesByScope[scopeType]?.[String(scopeId)] ?? `#${scopeId}`;
    };

    const subscriptionGroups = useMemo((): SubscriptionGroup[] => {
        const byEmailScope = new Map<string, SubscriptionGroup>();
        (subscriptionRows ?? []).forEach((subscription) => {
            const key = `${subscription.Email.toLowerCase()}|${subscription.ScopeType}|${subscription.ScopeID}`;
            const group = byEmailScope.get(key) ?? {
                email: subscription.Email,
                scopeType: subscription.ScopeType,
                scopeId: subscription.ScopeID,
                topics: [],
            };
            group.topics.push(subscription.Topic);
            byEmailScope.set(key, group);
        });
        return Array.from(byEmailScope.values());
    }, [subscriptionRows]);

    const canSubscribe =
        selectedEmails.length > 0 && selectedTopicOptions.length > 0 && effectiveOrgs.length > 0 && !subscribing;

    const handleSubscribe = async () => {
        await subscribe({
            contact: {
                Emails: selectedEmails.map((option) => option.name),
                Topics: selectedTopicOptions.map((option) => option.id as NotificationTopic),
                Scopes: effectiveOrgs.map((option) => parseOrgOptionId(option.id)),
            },
        });
        setSelectedEmails([]);
        setSelectedTopicOptions([]);
        setSelectedOrgs([]);
    };

    const handleRemove = (group: SubscriptionGroup) => {
        unsubscribe({
            contact: { Email: group.email, ScopeType: group.scopeType, ScopeID: group.scopeId },
        });
    };

    return (
        <>
            <InfoCard
                title='Get Notifications'
                icon={<NotificationsNoneOutlinedIcon />}
                note='Pick who gets emailed, for which topics, across which organizations. Company-level subscriptions cover everything under the company, including its resellers.'>
                <Grid container spacing={2} alignItems='flex-start'>
                    <Grid item xs={12} md={4}>
                        <MultiSelectField
                            label='Emails'
                            freeSolo
                            validateInput={(rawValue) => Validate.optionalEmail(rawValue)}
                            value={selectedEmails}
                            options={emailSuggestions}
                            onChange={setSelectedEmails}
                            placeholder='Type an address, press Enter'
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <MultiSelectField
                            label='Topics'
                            value={selectedTopicOptions}
                            options={topicOptions}
                            onChange={setSelectedTopicOptions}
                            placeholder='Select topics'
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <MultiSelectField
                            label='Organizations'
                            value={effectiveOrgs}
                            options={orgOptions}
                            onChange={setSelectedOrgs}
                            placeholder='Search organizations'
                        />
                    </Grid>
                    <Grid item xs={12} md={2}>
                        {/* Top offset matches the fields' label band on desktop; stacked mobile needs none */}
                        <Box sx={{ pt: { xs: 0, md: '26px' } }}>
                            <Button
                                variant='primary'
                                fullWidth
                                disabled={!canSubscribe}
                                loading={subscribing}
                                onClick={handleSubscribe}>
                                Subscribe
                            </Button>
                        </Box>
                    </Grid>
                </Grid>
                <Typography sx={{ mt: 1, fontSize: typographyTokens.fontSize.xs, color: colors.neutral[500] }}>
                    Your email is suggested first; invalid addresses are ignored. Organization choices follow the
                    selected topics' allowed scopes.
                </Typography>
            </InfoCard>

            <InfoCard title='Current subscriptions' icon={<MailOutlineIcon />}>
                {subscriptionsLoading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress size={28} sx={{ color: colors.primary[500] }} />
                    </Box>
                )}

                {!subscriptionsLoading && !subscriptionGroups.length && (
                    <EmptyState
                        title='No subscriptions yet'
                        description='Subscribe an email above and it will show up here.'
                        icon={<MailOutlineIcon />}
                    />
                )}

                {subscriptionGroups.map((group) => (
                    <Box
                        key={`${group.email}-${group.scopeType}-${group.scopeId}`}
                        sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', sm: 'row' },
                            alignItems: { xs: 'stretch', sm: 'center' },
                            gap: { xs: 0.75, sm: 1.25 },
                            py: 1.25,
                            borderBottom: `1px solid ${colors.neutral[150]}`,
                            '&:last-of-type': { borderBottom: 'none' },
                        }}>
                        {/* Email line; on mobile the remove button pins to its right */}
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                flex: { sm: 1 },
                                minWidth: 0,
                                order: { xs: 0, sm: 0 },
                            }}>
                            <Typography
                                noWrap
                                sx={{
                                    fontFamily: typographyTokens.fontFamily.mono,
                                    fontSize: typographyTokens.fontSize.sm,
                                    color: colors.neutral[900],
                                    flex: 1,
                                    minWidth: 0,
                                }}>
                                {group.email}
                            </Typography>
                            <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
                                <IconButton
                                    icon={<CloseIcon fontSize='small' />}
                                    tooltip={`Remove ${group.email} at this organization`}
                                    variant='ghost'
                                    size='small'
                                    disabled={unsubscribing}
                                    onClick={() => handleRemove(group)}
                                />
                            </Box>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                            {group.topics.map((topic) => (
                                <Chip
                                    key={topic}
                                    size='small'
                                    label={topicLabels[topic] ?? topic}
                                    sx={{
                                        backgroundColor: colors.primary[100],
                                        color: colors.primary[700],
                                        fontWeight: typographyTokens.fontWeight.medium,
                                        fontSize: typographyTokens.fontSize.xs,
                                    }}
                                />
                            ))}
                        </Box>

                        <Box sx={{ alignSelf: { xs: 'flex-start', sm: 'center' } }}>
                            <StatusBadge
                                label={`${group.scopeType} · ${scopeName(group.scopeType, group.scopeId)}`}
                                status='neutral'
                                size='small'
                            />
                        </Box>

                        <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                            <IconButton
                                icon={<CloseIcon fontSize='small' />}
                                tooltip={`Remove ${group.email} at this organization`}
                                variant='ghost'
                                size='small'
                                disabled={unsubscribing}
                                onClick={() => handleRemove(group)}
                            />
                        </Box>
                    </Box>
                ))}
            </InfoCard>
        </>
    );
};

export default GetNotificationsCard;
